import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FormData } from "../AdvisorWizard";
import { Target, DollarSign, Shield, Zap, Settings, Home, Users, HelpCircle } from "lucide-react";

interface PreferencesStepProps {
  data: FormData;
  onComplete: (data: Partial<FormData>) => void;
}

const PreferencesStep = ({ data, onComplete }: PreferencesStepProps) => {
  const [formData, setFormData] = useState<Partial<FormData>>({
    paymentForm: data.paymentForm || '',
    priority: data.priority || '',
    monthlyPayments: data.monthlyPayments || '',
    futurePlans: data.futurePlans || ''
  });

  const paymentForms = [
    { value: 'Einmalzahlung', label: 'Einmalzahlung', subtitle: 'Kompletter Betrag sofort', icon: DollarSign },
    { value: 'Monatliche Rente', label: 'Monatliche Rente', subtitle: 'Regelmäßige Zahlungen', icon: Target },
    { value: 'Kombination', label: 'Kombination', subtitle: 'Teil sofort, Teil als Rente', icon: Settings }
  ];

  const priorities = [
    { value: 'Höchstmöglicher Erlös', label: 'Höchstmöglicher Erlös', subtitle: 'Maximaler finanzieller Ertrag', icon: DollarSign },
    { value: 'Sicherheit', label: 'Sicherheit', subtitle: 'Minimales Risiko', icon: Shield },
    { value: 'Flexibilität', label: 'Flexibilität', subtitle: 'Anpassungsmöglichkeiten', icon: Zap },
    { value: 'Einfachheit', label: 'Einfachheit', subtitle: 'Unkomplizierte Abwicklung', icon: Settings }
  ];

  const monthlyPaymentOptions = [
    { value: 'Ja', label: 'Ja', subtitle: 'Bereit für laufende Kosten' },
    { value: 'Nein', label: 'Nein', subtitle: 'Keine monatlichen Belastungen gewünscht' },
    { value: 'Kommt darauf an', label: 'Kommt darauf an', subtitle: 'Situationsabhängig' }
  ];

  const futurePlansOptions = [
    { value: 'Lebenslang in der Immobilie bleiben', label: 'Lebenslang in der Immobilie bleiben', subtitle: 'Dauerhaftes Wohnen', icon: Home },
    { value: 'Eventuell ins Pflegeheim', label: 'Eventuell ins Pflegeheim', subtitle: 'Möglicher Umzug', icon: Users },
    { value: 'Zu Familie ziehen', label: 'Zu Familie ziehen', subtitle: 'Familiärer Umzug geplant', icon: Users },
    { value: 'Bin noch unentschieden', label: 'Bin noch unentschieden', subtitle: 'Unklare Zukunft', icon: HelpCircle }
  ];

  const handleOptionSelect = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const isComplete = () => {
    return formData.paymentForm && formData.priority && formData.monthlyPayments && formData.futurePlans;
  };

  const handleNext = () => {
    if (isComplete()) {
      onComplete(formData);
    }
  };

  return (
    <Card className="card-elevated">
      <CardHeader className="text-center pb-6">
        <div className="bg-accent/10 p-4 rounded-xl w-fit mx-auto mb-4">
          <Target className="h-8 w-8 text-accent" />
        </div>
        <CardTitle className="text-2xl md:text-3xl text-primary">
          Schritt 3: Ihre Wünsche und Ziele
        </CardTitle>
        <p className="text-lg text-muted-foreground">
          Lassen Sie uns wissen, was Ihnen bei der Verrentung am wichtigsten ist.
        </p>
      </CardHeader>
      
      <CardContent className="space-y-8">
        {/* Auszahlungsform */}
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-primary flex items-center gap-2">
            <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm">1</span>
            Gewünschte Auszahlungsform
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {paymentForms.map((option) => {
              const IconComponent = option.icon;
              return (
                <button
                  key={option.value}
                  onClick={() => handleOptionSelect('paymentForm', option.value)}
                  className={`p-3 sm:p-4 text-center border rounded-lg transition-all hover:shadow-md min-h-[90px] flex flex-col items-center justify-center ${
                    formData.paymentForm === option.value
                      ? 'border-accent bg-accent/5 shadow-md'
                      : 'border-border hover:border-accent/50'
                  }`}
                >
                  <IconComponent className="h-5 w-5 sm:h-6 sm:w-6 mx-auto mb-2 text-accent flex-shrink-0" />
                  <div className="font-semibold text-primary text-xs sm:text-sm leading-tight break-words w-full">{option.label}</div>
                  <div className="text-xs text-muted-foreground leading-tight break-words w-full">{option.subtitle}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Priorität */}
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-primary flex items-center gap-2">
            <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm">2</span>
            Was ist Ihnen am wichtigsten?
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {priorities.map((option) => {
              const IconComponent = option.icon;
              return (
                <button
                  key={option.value}
                  onClick={() => handleOptionSelect('priority', option.value)}
                  className={`p-3 sm:p-4 text-left border rounded-lg transition-all hover:shadow-md min-h-[80px] flex items-center ${
                    formData.priority === option.value
                      ? 'border-accent bg-accent/5 shadow-md'
                      : 'border-border hover:border-accent/50'
                  }`}
                >
                  <div className="flex items-start gap-3 w-full">
                    <IconComponent className="h-5 w-5 sm:h-6 sm:w-6 text-accent mt-1 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-primary text-xs sm:text-sm leading-tight break-words">{option.label}</div>
                      <div className="text-xs text-muted-foreground leading-tight break-words">{option.subtitle}</div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Monatliche Zahlungen */}
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-primary flex items-center gap-2">
            <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm">3</span>
            Bereitschaft für monatliche Zahlungen
          </h3>
          <p className="text-muted-foreground">
            Manche Verrentungsarten erfordern monatliche Zahlungen (z.B. Nutzungsentgelte beim Teilverkauf)
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {monthlyPaymentOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => handleOptionSelect('monthlyPayments', option.value)}
                className={`p-3 sm:p-4 text-center border rounded-lg transition-all hover:shadow-md min-h-[70px] flex flex-col items-center justify-center ${
                  formData.monthlyPayments === option.value
                    ? 'border-accent bg-accent/5 shadow-md'
                    : 'border-border hover:border-accent/50'
                }`}
              >
                <div className="font-semibold text-primary text-xs sm:text-sm leading-tight break-words w-full">{option.label}</div>
                <div className="text-xs text-muted-foreground leading-tight break-words w-full">{option.subtitle}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Zukunftspläne */}
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-primary flex items-center gap-2">
            <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm">4</span>
            Ihre Zukunftspläne
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {futurePlansOptions.map((option) => {
              const IconComponent = option.icon;
              return (
                <button
                  key={option.value}
                  onClick={() => handleOptionSelect('futurePlans', option.value)}
                  className={`p-3 sm:p-4 text-left border rounded-lg transition-all hover:shadow-md min-h-[80px] flex items-center ${
                    formData.futurePlans === option.value
                      ? 'border-accent bg-accent/5 shadow-md'
                      : 'border-border hover:border-accent/50'
                  }`}
                >
                  <div className="flex items-start gap-3 w-full">
                    <IconComponent className="h-5 w-5 sm:h-6 sm:w-6 text-accent mt-1 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-primary text-xs sm:text-sm leading-tight break-words">{option.label}</div>
                      <div className="text-xs text-muted-foreground leading-tight break-words">{option.subtitle}</div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Weiter Button */}
        <div className="pt-6 flex justify-center">
          <Button 
            variant="accent" 
            size="lg" 
            onClick={handleNext}
            disabled={!isComplete()}
            className="text-base sm:text-lg md:text-xl px-6 py-3 sm:px-8 md:px-12 h-auto leading-tight text-center whitespace-normal max-w-xs sm:max-w-sm md:max-w-none"
          >
            <span className="block">
              Empfehlungen<br className="sm:hidden" />
              <span className="sm:ml-1">berechnen</span>
            </span>
          </Button>
          {!isComplete() && (
            <p className="text-sm text-muted-foreground mt-2">
              Bitte beantworten Sie alle Fragen, um Ihre Empfehlungen zu erhalten
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default PreferencesStep;